import { useCallback, useEffect, useRef, useState } from 'react';
import { UploadCancelledError, uploadFileWithProgress } from '../lib/file-upload-transfer';

const MAX_CONCURRENT_UPLOADS = 3;
const SUCCESS_DISMISS_MS = 4_000;

export type FileUploadStage =
  | 'queued'
  | 'requesting'
  | 'uploading'
  | 'registering'
  | 'success'
  | 'error'
  | 'cancelled';

export interface FileUploadStatus {
  key: string;
  file: File;
  status: FileUploadStage;
  progress: number;
  uploadedBytes: number;
  totalBytes: number;
  error?: string;
  fileId?: number;
}

export interface ManagedFileUploadTransport {
  cleanupUpload: (r2Path: string) => Promise<void>;
  getUploadUrls: (files: File[]) => Promise<Array<{ uploadUrl: string; r2Path: string }>>;
  isRequestingUploadUrls: boolean;
  registerFile: (file: File, r2Path: string, category?: string) => Promise<number>;
}

function uploadKey(file: File, index: number) {
  return `${file.name}-${file.size}-${file.lastModified}-${index}`;
}

export function useManagedFileUpload({
  cleanupUpload,
  getUploadUrls,
  isRequestingUploadUrls,
  registerFile,
}: ManagedFileUploadTransport) {
  const [uploadStatuses, setUploadStatuses] = useState<Record<string, FileUploadStatus>>({});
  const controllers = useRef(new Map<string, AbortController>());
  const cancelledKeys = useRef(new Set<string>());
  const statuses = useRef(uploadStatuses);
  const successTimers = useRef(new Map<string, number>());

  useEffect(
    () => () => {
      successTimers.current.forEach((timer) => {
        window.clearTimeout(timer);
      });
    },
    []
  );

  const dismissSuccess = useCallback((key: string) => {
    window.clearTimeout(successTimers.current.get(key));

    const timer = window.setTimeout(() => {
      successTimers.current.delete(key);
      setUploadStatuses((previous) => {
        if (previous[key]?.status !== 'success') return previous;

        const { [key]: _dismissed, ...next } = previous;
        statuses.current = next;
        return next;
      });
    }, SUCCESS_DISMISS_MS);

    successTimers.current.set(key, timer);
  }, []);

  const updateStatus = useCallback((key: string, update: Partial<FileUploadStatus>) => {
    setUploadStatuses((previous) => {
      const next = { ...previous, [key]: { ...previous[key], ...update } };
      statuses.current = next;
      return next;
    });
  }, []);

  const uploadOne = useCallback(
    async ({
      category,
      file,
      key,
      r2Path,
      uploadUrl,
    }: {
      category?: string;
      file: File;
      key: string;
      r2Path: string;
      uploadUrl: string;
    }): Promise<number | null> => {
      if (cancelledKeys.current.has(key)) {
        try {
          await cleanupUpload(r2Path);
        } catch {
          updateStatus(key, {
            error: 'Upload was cancelled but its temporary file could not be removed.',
            status: 'error',
          });
        }
        return null;
      }

      const controller = new AbortController();
      controllers.current.set(key, controller);
      updateStatus(key, { status: 'uploading' });

      try {
        await uploadFileWithProgress({
          file,
          signal: controller.signal,
          uploadUrl,
          onProgress: (uploadedBytes, totalBytes) => {
            updateStatus(key, {
              progress: totalBytes === 0 ? 100 : Math.round((uploadedBytes / totalBytes) * 100),
              totalBytes,
              uploadedBytes,
            });
          },
        });
        controllers.current.delete(key);

        if (cancelledKeys.current.has(key)) {
          await cleanupUpload(r2Path);
          updateStatus(key, { status: 'cancelled' });
          return null;
        }

        updateStatus(key, { progress: 100, status: 'registering', uploadedBytes: file.size });
        const fileId = await registerFile(file, r2Path, category);
        updateStatus(key, { fileId, status: 'success' });
        dismissSuccess(key);
        return fileId;
      } catch (error) {
        controllers.current.delete(key);

        if (error instanceof UploadCancelledError) {
          try {
            await cleanupUpload(r2Path);
            updateStatus(key, { status: 'cancelled' });
          } catch {
            updateStatus(key, {
              error: 'Upload was cancelled but its temporary file could not be removed.',
              status: 'error',
            });
          }
          return null;
        }
        try {
          await cleanupUpload(r2Path);
        } catch {
          // The primary failure stays visible; cleanup failures are already bounded to an unregistered key.
        }

        updateStatus(key, {
          error: error instanceof Error ? error.message : 'Upload failed',
          status: 'error',
        });
        return null;
      }
    },
    [cleanupUpload, dismissSuccess, registerFile, updateStatus]
  );

  const uploadAll = useCallback(
    async (files: File[], category?: string) => {
      if (files.length === 0) return [];

      const entries = files.map((file, index) => {
        const key = uploadKey(file, index);
        return {
          file,
          key,
          status: {
            file,
            key,
            progress: 0,
            status: 'requesting' as const,
            totalBytes: file.size,
            uploadedBytes: 0,
          },
        };
      });
      const initialStatuses = Object.fromEntries(entries.map(({ key, status }) => [key, status]));
      for (const { key } of entries) {
        window.clearTimeout(successTimers.current.get(key));
        successTimers.current.delete(key);
      }
      setUploadStatuses((previous) => {
        const next = { ...previous, ...initialStatuses };
        statuses.current = next;
        return next;
      });

      let urls: Array<{ uploadUrl: string; r2Path: string }>;
      try {
        urls = await getUploadUrls(files);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Could not prepare file uploads';
        for (const { key } of entries) updateStatus(key, { error: message, status: 'error' });
        throw error;
      }

      if (urls.length !== entries.length) {
        const error = new Error('Could not prepare every selected file for upload');
        for (const { key } of entries) updateStatus(key, { error: error.message, status: 'error' });
        throw error;
      }

      const results: Array<number | null> = Array.from({ length: entries.length }, () => null);
      let nextEntry = 0;
      const workers = Array.from(
        { length: Math.min(MAX_CONCURRENT_UPLOADS, entries.length) },
        async () => {
          while (nextEntry < entries.length) {
            const entryIndex = nextEntry;
            nextEntry += 1;
            const entry = entries[entryIndex];
            const url = urls[entryIndex];
            results[entryIndex] = await uploadOne({ ...entry, ...url, category });
          }
        }
      );

      await Promise.all(workers);
      return results.filter((fileId): fileId is number => fileId !== null);
    },
    [getUploadUrls, updateStatus, uploadOne]
  );

  const cancelUpload = useCallback(
    (key: string) => {
      const status = statuses.current[key];
      if (!status || !['requesting', 'uploading'].includes(status.status)) return;

      cancelledKeys.current.add(key);
      const controller = controllers.current.get(key);
      if (controller) controller.abort();
      else updateStatus(key, { status: 'cancelled' });
    },
    [updateStatus]
  );

  return {
    cancelUpload,
    isUploading:
      isRequestingUploadUrls ||
      Object.values(uploadStatuses).some(({ status }) =>
        ['queued', 'requesting', 'uploading', 'registering'].includes(status)
      ),
    uploadAll,
    uploadStatuses,
  };
}
